import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { BEFORE_YOU_BOOK, SAFETY_FACTS, WHY_NOT_CHEAPEST } from "@/lib/safety-facts";
import { promMinimumFaq } from "@/lib/booking-minimums";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-limo-3.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=900&imagetools";
import galleryB from "@/assets/sprinter-entry.jpg?format=webp&quality=72&w=900&imagetools";
import galleryC from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=900&imagetools";
import promGroup from "@/assets/prom-night-chauffeur-group-atlanta.jpg?format=webp&quality=72&w=800&imagetools";
import promCouple from "@/assets/prom-couple-chauffeur-atlanta.jpg?format=webp&quality=72&w=800&imagetools";
import introImage from "@/assets/sprinter-limo-hotel-arrival-atlanta.jpg?format=webp&quality=72&w=1200&imagetools";
import entryLarge from "@/assets/sprinter-entry.jpg?format=webp&quality=72&w=1200&imagetools";
import limoParty from "@/assets/sprinter-limo-party.jpg?format=webp&quality=72&w=900&imagetools";
import orangeLounge from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=900&imagetools";
import heroLarge from "@/assets/sprinter-limo-3.jpg?format=webp&quality=72&w=900&imagetools";
import faqImage from "@/assets/sprinter-party.jpg?format=webp&quality=72&w=900&imagetools";
import quinceCard from "@/assets/quinceanera-sprinter-limo-sunset-atlanta.jpg?format=webp&quality=70&w=800&imagetools";
import weddingCard from "@/assets/wedding-bride-veil-celebration-atlanta.jpg?format=webp&quality=70&w=800&imagetools";
import limoCard from "@/assets/sprinter-limo-2.jpg?format=webp&quality=70&w=800&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/prom-limo-atlanta";
const PAGE_TITLE = "Prom Limo Atlanta | Sprinter Limo for Prom Night";
const PAGE_DESC =
  "Chauffeured Mercedes-Benz Sprinter Limousine for Atlanta prom night. Up to 14 friends, one chauffeur, no alcohol on board. From $250/hour. Call (678) 431-7222.";

const SAFETY = "Safety & Licensing";
const PRICING = "Pricing & Booking";
const NIGHT = "On Prom Night";

const faqs = [
  {
    group: SAFETY,
    q: "How do I know a prom limo company is legal in Georgia?",
    a: "Every limousine company in Georgia needs a certificate from the Georgia Department of Public Safety, and the department publishes a list of licensed limousine carriers. Check that the company is on it, ask for proof of commercial insurance, and ask whether every chauffeur is fingerprinted and background-checked. We are registered with the State of Georgia, operate 100% legally and carry commercial insurance.",
  },
  {
    group: SAFETY,
    q: "Are your chauffeurs background-checked?",
    a: "Yes. Every chauffeur is fingerprinted and background-checked before driving for us, and most have been with my family for years. Our vehicles also have cameras and AI driver-safety technology.",
  },
  {
    group: SAFETY,
    q: "Is alcohol allowed on a prom ride?",
    a: "No. Every prom ride is alcohol-free. BYOB applies only to guests 21+, and we never sell or provide alcohol.",
  },
  {
    group: SAFETY,
    q: "Can parents follow the ride?",
    a: "Yes. Track your chauffeur live in our app, so you always know when they'll pull up.",
  },
  {
    group: PRICING,
    q: "How much does a prom limo cost in Atlanta?",
    a: "Sprinters start from $250 per hour. Every prom gets a custom quote, given upon request, based on the hours, the number of pickups and the vehicle. A 50% deposit secures your date, and your final quote is always in writing before you book, so it is easy to share with the other parents.",
  },
  promMinimumFaq({ group: PRICING }),
  sameCompanyFaq({ group: PRICING }),
  {
    group: PRICING,
    q: "Why do some prom limo quotes cost so much less?",
    a: "Doing this legally costs real money: state certification, commercial insurance, fingerprinting and background checks, and safety technology in the vehicles. Some companies quote a low price because they skip those steps and operate illegally. We don't cut corners, and our price reflects what it actually costs to keep your kids safe.",
  },
  {
    group: PRICING,
    q: "How many students fit in the Sprinter for prom?",
    a: "The Sprinter Limousine (Limo Style) seats up to 14 and the Executive Sprinter up to 13. For a smaller group, the Jet Sprinter seats up to 7, and the Suburban SUV seats up to 6 with captain seats or 7 with a bench.",
  },
  {
    group: NIGHT,
    q: "Does the chauffeur wait during the dance?",
    a: "Yes. Bookings are hourly, so the chauffeur stays with the Sprinter for the whole booking, including the time the group is inside at dinner or the dance.",
  },
  {
    group: NIGHT,
    q: "Can you pick up students at different homes?",
    a: "Yes. Text me each address and we'll plan the stops in order. Extra stops add time, which I include in your quote.",
  },
];

export const Route = createFileRoute("/prom-limo-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "prom limo atlanta, prom limo service atlanta, prom party bus atlanta, sprinter limo prom, prom transportation atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Prom Limo Atlanta",
            image: "https://atlantaluxurysprinters.com/favicon.png",
            description: PAGE_DESC,
            serviceType: "Prom Limousine Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Alpharetta" },
              { type: "City", name: "Marietta" },
              { type: "City", name: "Sandy Springs" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Prom Night • Metro Atlanta"
      h1="Prom Limo Service in Atlanta"
      intro="You've planned this night for months. I'll keep the group together from photos to dinner, the dance and home, with a professional chauffeur and no alcohol on board."
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter Limousine cabin with ambient ceiling lights, ready for prom night in Atlanta"
      rich
      quickFacts={[
        { value: "Licensed", label: "Registered with the State of Georgia" },
        { value: "Insured", label: "Commercial insurance, every ride" },
        { value: "Fingerprinted", label: "Background-checked chauffeurs" },
        { value: "No alcohol", label: "On board for prom" },
      ]}
      introImage={introImage}
      introImageAlt="Black Mercedes-Benz Sprinter Limousine at a hotel entrance in Atlanta"
      bookingBands={[
        { title: "Check Your Prom Date", body: "Send us the prom date, the group size and the pickup addresses. We reply with a written quote." },
        { title: "Reserve the Sprinter for Prom", body: "One quote for the whole group, in writing, so parents can split it. A 50% deposit secures your date." },
      ]}
      faqImage={faqImage}
      faqImageAlt="Sprinter Limousine lounge with green LED ceiling and wraparound seating"
      midImage={midImage}
      midAlt="Sprinter Limousine leather seating with purple mood lighting for a prom group"
      sectionOneTitle="Let's Plan Prom Together"
      sectionOneBody={[
        "I know what you're trusting us with. Tell me the schedule and stops; my chauffeur stays with the Sprinter through photos, dinner, the dance and home.",
        "Prom rides are alcohol-free. We own every vehicle in our fleet, so your group rides in our Sprinter with our chauffeur.",
      ]}
      bulletsTitle="What Your Prom Booking Includes"
      bulletsIntro="One Sprinter and one chauffeur stay with your group for the booked hours."
      bullets={[
        "Seating for up to 14 in the Sprinter Limousine, or 13 in the Executive Sprinter",
        "Wavy LED ceiling and club-quality sound on both walls",
        "Wraparound leather seating, so the whole group rides together",
        "Built-in cooler for the group's water and soft drinks",
        "One chauffeur who stays with the Sprinter all night",
        "Track your chauffeur live in our app, so you always know when they'll pull up",
        "No alcohol on board",
      ]}
      coverageTitle="Prom Pickups Across Metro Atlanta"
      coverageIntro="Home pickups, dinner reservations and school proms at hotels and venues across metro Atlanta."
      coverage={[
        "Buckhead & Midtown",
        "Sandy Springs & Dunwoody",
        "Alpharetta & Johns Creek",
        "Roswell & Milton",
        "Marietta & Kennesaw",
        "Smyrna & Vinings",
        "Decatur & DeKalb",
        "Gwinnett County",
        "South Fulton & Fayette",
      ]}
      detailSections={[
        {
          title: "Before You Book a Prom Limo: What Parents Should Check",
          body: [
            "A cheap prom quote is easy to find. A company that is legal, insured and careful about who drives your kids is harder. Ask every company these questions before you pay a deposit, including us:",
          ],
          list: [
            ...BEFORE_YOU_BOOK,
            "What is the alcohol policy? For prom, the answer should be no alcohol on board.",
          ],
        },
        {
          title: "How We Keep Your Kids Safe",
          body: [
            "Prom is often the first big night out without a parent in the car. This is what stands behind every prom ride we run:",
          ],
          list: [...SAFETY_FACTS, "No alcohol on prom rides, for anyone."],
          image: promCouple,
          imageAlt: "Prom couple with a bouquet standing next to their chauffeur",
          imageWhole: true,
        },
        {
          title: "Don't Pay for a Cheap, Illegal Ride",
          body: [
            WHY_NOT_CHEAPEST,
            "We would rather earn the families who value that than compete with companies that cut corners on their kids' safety.",
          ],
        },
        {
          title: "A Typical Prom Night",
          body: [
            "Every group plans it differently. This is the order most prom nights follow, and the chauffeur runs it on the schedule you set.",
          ],
          list: [
            "Pickup at the house where the group takes photos.",
            "Dinner, with the chauffeur waiting with the Sprinter.",
            "Drop-off at the dance venue.",
            "Pickup when the dance ends.",
            "Drop-offs at each home, or one stop where parents meet the group.",
          ],
          image: entryLarge,
          imageAlt: "Sprinter Limousine entry with red floor lighting, ready for prom photos",
        },
        {
          title: "Splitting the Cost",
          body: [
            "Prom groups usually share one hourly booking. Sprinters start from $250 per hour with a 6-hour minimum for prom, and every quote is custom and in writing, so one parent can book and share the exact number with the others. A 50% deposit secures your date.",
          ],
        },
      ]}
      gallery={{
        title: "The Prom Night Fleet",
        intro: "Sprinter Limousines for the full group, plus Executive Sprinters with individual seats.",
        images: [
          { src: galleryA, alt: "Sprinter Limousine lounge with wraparound leather seating and ambient lighting", caption: "Sprinter Limousine, up to 14" },
          { src: galleryB, alt: "Sprinter Limousine entry with red floor lighting", caption: "Lit entry for the group photos" },
          { src: galleryC, alt: "Executive Sprinter cabin with green ambient lighting", caption: "Executive Sprinter, up to 13" },
          { src: limoParty, alt: "Sprinter Limousine with purple LED ceiling", caption: "LED ceiling in the group's colors" },
          { src: orangeLounge, alt: "Sprinter Limousine wraparound seating with orange lighting and a flat-screen TV", caption: "Wraparound seating, face to face" },
          { src: heroLarge, alt: "Sprinter Limousine cabin with ambient ceiling lights", caption: "Club-quality sound on both walls" },
        ],
      }}
      reviews={{
        title: "What Parents Say After Prom",
        intro: "Real prom nights, in the words of the parents who booked them.",
        photos: [
          { src: promGroup, alt: "Two prom couples in red gowns and tuxedos with their chauffeur beside the vehicle" },
        ],
        photoCredit: "Prom night photo shared by Meredith S. in her Google review: \"Our driver Tony was amazing taking our kids for their prom date.\"",
        items: [
          {
            name: "Deschell A.",
            date: "March 2025",
            text: "Our girls had a blast on their senior prom night! The sprinter was perfect! Super clean, comfy and a great sound system. Kevin was the absolute best driver! He was on time, took excellent care of the girls and checked in with me at every stop!",
          },
          {
            name: "Tammy W.",
            date: "March 2026",
            text: "Jjuan was an excellent driver. He was driving our kids to prom and he kept us updated on the drive. He even sent video showing the kids had been dropped off at the correct location. The kids all bragged on how nice he was. 10 out of 10 and we will use Empress Elite again.",
          },
          {
            name: "April W.",
            date: "July 2021",
            text: "Empress Elite helped make our daughter's prom night with friends perfect! Everyone had a great time, and the driver was on time for pick up, dinner, the big event, and arriving home. I had never let my daughter go with friends (without family) until now, and after a great experience, I would recommend Empress to everyone. They put safety first!",
          },
          {
            name: "Akia S.",
            date: "March 2024",
            text: "Empress Elite Limousines did an amazing job with the transport of my daughter and her friends to their prom. They were prompt, polite, and professional keeping me informed with status updates throughout the time frame of service.",
          },
          {
            name: "Beth D.",
            date: "March 2025",
            text: "We used Empress Elite for Prom and they did a great job!! Torrod got the kids to multiple stops with out any issues. Many of the kids said the limo was their favorite part of the night!!",
          },
        ],
      }}
      faqs={faqs}
      faqTitle="Prom Limo FAQs"
      relatedTitle="Related Occasion Services"
      related={[
        { to: "/quinceanera-limo-atlanta", title: "Quinceañera Limo Atlanta", desc: "Room for the ball gown and the whole court.", image: quinceCard, imageWhole: true },
        { to: "/wedding-transportation-atlanta", title: "Wedding Transportation", desc: "Bridal party transfers, the couple's ride and guest loops.", image: weddingCard, imageWhole: true },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The full Mercedes-Benz Sprinter Limousine fleet.", image: limoCard },
      ]}
      ctaTitle="Text Me Your Prom Date"
      ctaBody="Send me the date, pickup addresses and group size. I'll check availability myself and send a written quote you can share with the other parents."
    />
  );
}
